"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.listExpenseCategoriesQuerySchema = exports.updateExpenseCategorySchema = exports.createExpenseCategorySchema = exports.listSuppliersQuerySchema = exports.updateSupplierSchema = exports.createSupplierSchema = exports.bulkExpenseCategorySchema = exports.createExpensePaymentSchema = exports.listExpenseBankTxQuerySchema = exports.listExpensesQuerySchema = exports.updateExpenseSchema = exports.createExpenseSchema = void 0;
const zod_1 = require("zod");
const enums_1 = require("../enums");
const currency_1 = require("../currency");
const money_1 = require("../money");
const common_schema_1 = require("./common.schema");
const EXPENSE_KIND_VALUES = Object.values(enums_1.ExpenseKind);
const EXPENSE_SOURCE_VALUES = Object.values(enums_1.ExpenseSource);
const PAYMENT_STATUS_VALUES = Object.values(enums_1.PaymentStatus);
/** Money as entered by a user: non-negative, up to 2 decimals. */
const UNSIGNED_MONEY_REGEX = /^\d+(\.\d{1,2})?$/;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ZERO_REGEX = /^-?0+(\.0+)?$/;
// Local copy of invoice.schema.ts's `isoDateField` (each schema file in this
// package keeps its own — see the note in subscription.schema.ts).
function isRealIsoDate(value) {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}
const isoDateField = zod_1.z
    .string()
    .trim()
    .regex(ISO_DATE_REGEX, 'Data trebuie să fie în formatul YYYY-MM-DD.')
    .refine(isRealIsoDate, 'Dată invalidă.');
const unsignedMoneyField = zod_1.z
    .string()
    .trim()
    .regex(UNSIGNED_MONEY_REGEX, 'Suma trebuie să fie un număr pozitiv cu cel mult 2 zecimale.');
const nonZeroMoneyField = unsignedMoneyField.refine((value) => !ZERO_REGEX.test(value), 'Suma nu poate fi zero.');
/** Original document currency — unlike invoices (RON-only), an expense keeps
 * the supplier's currency (e.g. a USD Anthropic invoice paid in RON).
 * Normalized BEFORE validation so the leu aliases a client may forward
 * verbatim from a document ("LEI"/"lei"/"leu") land as RON — the only
 * spelling the BNR conversion treats as the identity. */
const currencyField = zod_1.z
    .string()
    .transform(currency_1.normalizeCurrency)
    .pipe(zod_1.z.string().regex(/^[A-Z]{3}$/, 'Moneda trebuie să fie un cod ISO de 3 litere (ex. RON, EUR).'));
/**
 * When BOTH net and VAT are present, they must add up to the total within
 * 1 ban — supplier documents legitimately round differently, but a larger
 * gap means a typo in one of the three amounts.
 */
function amountsAddUp(data) {
    if (!data.netAmount || !data.vatAmount || !data.totalAmount)
        return true;
    const diff = (0, money_1.subtractMoney)((0, money_1.subtractMoney)(data.totalAmount, data.netAmount), data.vatAmount);
    return diff === '0.00' || diff === '0.01' || diff === '-0.01';
}
// Bare (undefaulted) field definitions shared by create and update — same
// reasoning as invoice.schema.ts's `invoiceFields`: `.default()` on a shared
// shape would fire on absent PATCH fields after `.partial()`. Defaults are
// layered on only in `createExpenseSchema`.
//
// `source` deliberately does NOT exist here: it records where the expense came
// from (SPV/SmartBill/bank/manual) and is set server-side by each flow.
const expenseFields = {
    kind: zod_1.z.enum(EXPENSE_KIND_VALUES, 'Tip de cheltuială invalid.'),
    /** Existing supplier — or none (bank fees / state taxes have no supplier). */
    supplierId: zod_1.z.uuid('supplierId invalid.').optional(),
    /** Inline supplier creation, used when supplierId is absent: the service
     * find-or-creates by CIF (then normalized name). */
    supplierName: zod_1.z.string().trim().min(1).optional(),
    supplierCif: zod_1.z.string().trim().min(2).max(24).optional(),
    categoryId: zod_1.z.uuid('categoryId invalid.').optional(),
    docNumber: zod_1.z.string().trim().min(1).max(64).optional(),
    docDate: isoDateField,
    dueDate: isoDateField.optional(),
    currency: currencyField,
    netAmount: unsignedMoneyField.optional(),
    vatAmount: unsignedMoneyField.optional(),
    totalAmount: nonZeroMoneyField,
    description: zod_1.z.string().trim().max(500).optional(),
    notes: zod_1.z.string().trim().max(2000).optional(),
};
exports.createExpenseSchema = zod_1.z
    .object({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
    ...expenseFields,
    currency: currencyField.default('RON'),
    /** Manual receipts and manual (external) invoices are considered paid the
     * moment they are recorded — the server derives the default from `kind`
     * when the field is absent, so the client only sends it to override. */
    settled: zod_1.z.boolean().optional(),
})
    .refine(amountsAddUp, {
    message: 'Valoare fără TVA + TVA nu corespund cu totalul (diferență mai mare de 0.01).',
    path: ['totalAmount'],
});
exports.updateExpenseSchema = zod_1.z
    .object(expenseFields)
    .partial()
    .refine(amountsAddUp, {
    message: 'Valoare fără TVA + TVA nu corespund cu totalul (diferență mai mare de 0.01).',
    path: ['totalAmount'],
});
exports.listExpensesQuerySchema = common_schema_1.paginationQuerySchema.extend({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
    kind: zod_1.z.enum(EXPENSE_KIND_VALUES, 'Tip de cheltuială invalid.').optional(),
    source: zod_1.z.enum(EXPENSE_SOURCE_VALUES, 'Sursă invalidă.').optional(),
    supplierId: zod_1.z.uuid('supplierId invalid.').optional(),
    categoryId: zod_1.z.uuid('categoryId invalid.').optional(),
    paymentStatus: zod_1.z.enum(PAYMENT_STATUS_VALUES, 'Status de plată invalid.').optional(),
    dateFrom: isoDateField.optional(),
    dateTo: isoDateField.optional(),
    sortBy: zod_1.z.enum(['docDate', 'supplier', 'total', 'paymentStatus']).optional(),
    sortOrder: zod_1.z.enum(['asc', 'desc']).optional(),
});
const BANK_TX_STATUS_VALUES = Object.values(enums_1.BankTxStatus);
/** The reconciliation workspace: stored DEBIT transactions with their
 * allocation state. CREDIT rows (client payments) never show here. `search`
 * (from paginationQuerySchema) matches counterparty name, description and
 * counterparty CIF. */
exports.listExpenseBankTxQuerySchema = common_schema_1.paginationQuerySchema.extend({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
    status: zod_1.z.enum(BANK_TX_STATUS_VALUES, 'Status invalid.').optional(),
    bankAccountId: zod_1.z.uuid('bankAccountId invalid.').optional(),
    dateFrom: isoDateField.optional(),
    dateTo: isoDateField.optional(),
});
/** One allocation of money to an expense — from a stored DEBIT bank
 * transaction (`bankTransactionId` set) or recorded manually (absent).
 * Always RON: the amount that actually left the account. */
exports.createExpensePaymentSchema = zod_1.z.object({
    bankTransactionId: zod_1.z.uuid('bankTransactionId invalid.').optional(),
    paidOn: isoDateField,
    amountRon: nonZeroMoneyField,
    notes: zod_1.z.string().trim().max(500).optional(),
});
/** Bulk-assign a category to many expenses at once (or clear it with a null
 * categoryId) — the expenses-list multi-select action. */
exports.bulkExpenseCategorySchema = zod_1.z.object({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
    expenseIds: zod_1.z.array(zod_1.z.uuid('expenseId invalid.')).min(1, 'Selectează cel puțin o cheltuială.'),
    categoryId: zod_1.z.uuid('categoryId invalid.').nullable(),
});
// --- Suppliers ------------------------------------------------------------
const supplierFields = {
    name: zod_1.z.string().trim().min(1, 'Numele furnizorului este obligatoriu.'),
    // No CUI checksum here (unlike clients): imported supplier identifiers come
    // from bank statements / SmartBill / UBL and may be foreign or malformed at
    // the source — rejecting them would break imports. Stored normalized
    // (uppercase, no leading RO) by the service.
    cui: zod_1.z.string().trim().min(2).max(24).optional(),
    countryCode: zod_1.z
        .string()
        .trim()
        .regex(/^[A-Z]{2}$/, 'Codul de țară trebuie să fie 2 litere mari (ex. RO).')
        .optional(),
    iban: zod_1.z.string().trim().max(34).optional(),
    isActive: zod_1.z.boolean().optional(),
};
exports.createSupplierSchema = zod_1.z.object({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
    ...supplierFields,
});
exports.updateSupplierSchema = zod_1.z.object(supplierFields).partial();
exports.listSuppliersQuerySchema = common_schema_1.paginationQuerySchema.extend({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
});
// --- Expense categories ---------------------------------------------------
exports.createExpenseCategorySchema = zod_1.z.object({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
    name: zod_1.z.string().trim().min(1, 'Numele categoriei este obligatoriu.').max(120),
});
exports.updateExpenseCategorySchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Numele categoriei este obligatoriu.').max(120).optional(),
    isActive: zod_1.z.boolean().optional(),
});
exports.listExpenseCategoriesQuerySchema = zod_1.z.object({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
    includeInactive: zod_1.z
        .enum(['true', 'false'])
        .optional()
        .transform((value) => value === 'true'),
});
